import { View, Text, StyleProp, ViewStyle, Image } from 'react-native'
import { memo } from 'react'
import { getStyleSheet } from '../../assets/theme/styles'
import { MomentaryView } from './DataTable'
import useDimensions from '../../hooks/useDimensions'
import ItemSkeleton from './ItemSkeleton'
import Checkbox from 'expo-checkbox'

type ReportDetail = {
    createdDateFormat: string
    name?: string
    projectName?: string
    timeRange: string
    durationFormat: string
    pomodoroTime: string
    time: string
    id: string
    arrayTags: any[]
    createdOnlyDate: string
    _version: number
}

interface IReportDetails {
    reportDetails: ReportDetail
    isLoading: boolean
    isChildRow?: boolean
    isSelected: boolean
    setSelectedTasks: (task: { id: string; _version: number }) => void
}

const RowReportDetails = memo(
    ({ reportDetails, isLoading, isChildRow, isSelected, setSelectedTasks }: IReportDetails) => {
        const styles = getStyleSheet()
        const { windowWidth } = useDimensions()
        const largeSizeWidth = 1000

        const ComponentPomodoro = ({ customStyles }: { customStyles?: StyleProp<ViewStyle> }) => {
            return (
                <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 10 }, customStyles]}>
                    <Text style={{ color: styles.text, display: reportDetails.pomodoroTime ? 'flex' : 'none' }}>
                        {reportDetails.pomodoroTime}
                    </Text>
                    <Image source={require('../../assets/icons/pomodoro.png')} style={{ width: 23, height: 23 }} />
                </View>
            )
        }

        const ViewSkeleton = ({
            children,
            loading,
            skeletonStyles,
            customStyles,
        }: {
            children: React.ReactElement
            loading: boolean
            skeletonStyles?: StyleProp<ViewStyle>
            customStyles?: StyleProp<ViewStyle>
        }) => {
            if (loading) return <ItemSkeleton customStyles={skeletonStyles} />
            return <View style={customStyles}>{children}</View>
        }

        return (
            <View
                style={[
                    {
                        backgroundColor: styles.backgroundColorRow,
                        paddingVertical: 14,
                        paddingHorizontal: 20,
                        borderRadius: 6,
                        rowGap: 20,
                    },
                    windowWidth > largeSizeWidth ? { flexDirection: 'row', alignItems: 'center' } : null,
                ]}
            >
                <MomentaryView
                    customStyles={{ flexDirection: 'row', gap: 20, alignItems: 'center' }}
                    showViewUp={largeSizeWidth}
                >
                    <View style={{ width: '5%' }}>
                        <Checkbox
                            style={{ width: 20, height: 20 }}
                            value={isSelected}
                            onValueChange={() =>
                                setSelectedTasks({ id: reportDetails.id, _version: reportDetails._version })
                            }
                        />
                    </View>

                    <ViewSkeleton
                        loading={isLoading}
                        skeletonStyles={[windowWidth > largeSizeWidth ? { width: '25%', marginLeft: 25 } : null]}
                        customStyles={[
                            { flexGrow: 1, overflow: 'hidden', width: '100%' },
                            windowWidth > largeSizeWidth ? { width: '25%', paddingLeft: 25, flexGrow: 0 } : null,
                        ]}
                    >
                        <Text
                            ellipsizeMode="tail"
                            style={{
                                color: styles.text,
                                opacity: !reportDetails.projectName?.length ? 0.5 : 1,
                            }}
                        >
                            {reportDetails.name || '- No title -'}
                        </Text>
                    </ViewSkeleton>

                    {windowWidth <= largeSizeWidth ? <ComponentPomodoro /> : null}
                </MomentaryView>

                <MomentaryView
                    customStyles={{ flexDirection: 'row', justifyContent: 'space-between' }}
                    showViewUp={largeSizeWidth}
                >
                    <ViewSkeleton
                        loading={isLoading}
                        skeletonStyles={{ marginRight: 5 }}
                        customStyles={{ width: windowWidth > largeSizeWidth ? '10%' : 'auto' }}
                    >
                        <Text
                            style={{
                                color: styles.text,
                                textAlign: 'center',
                            }}
                        >
                            {reportDetails.projectName || '- No project -'}
                        </Text>
                    </ViewSkeleton>

                    {windowWidth <= largeSizeWidth ? (
                        <ViewSkeleton loading={isLoading}>
                            <Text style={{ color: styles.text }}>
                                {!isChildRow ? reportDetails.durationFormat : reportDetails.time}
                            </Text>
                        </ViewSkeleton>
                    ) : null}
                </MomentaryView>

                <MomentaryView
                    customStyles={{ flexDirection: 'row', justifyContent: 'space-between' }}
                    showViewUp={largeSizeWidth}
                >
                    <ViewSkeleton
                        loading={isLoading}
                        customStyles={{ width: windowWidth > largeSizeWidth ? '20%' : 'auto' }}
                    >
                        <Text numberOfLines={1} style={{ color: styles.text, textAlign: 'center' }}>
                            {reportDetails.timeRange}
                        </Text>
                    </ViewSkeleton>

                    {windowWidth > largeSizeWidth ? (
                        <ViewSkeleton loading={isLoading} customStyles={{ width: '10%' }}>
                            <Text style={{ color: styles.text, textAlign: 'center' }}>
                                {!isChildRow ? reportDetails.durationFormat : reportDetails.time}
                            </Text>
                        </ViewSkeleton>
                    ) : null}

                    {windowWidth > largeSizeWidth ? (
                        <ComponentPomodoro customStyles={{ width: '10%', justifyContent: 'center' }} />
                    ) : null}

                    <ViewSkeleton
                        loading={isLoading}
                        skeletonStyles={{ width: windowWidth > largeSizeWidth ? '20%' : '100%', marginLeft: 5 }}
                        customStyles={{ width: windowWidth > largeSizeWidth ? '20%' : 'auto' }}
                    >
                        <Text style={[{ color: styles.text, textAlign: 'center' }]}>
                            {reportDetails.createdOnlyDate}
                        </Text>
                    </ViewSkeleton>
                </MomentaryView>
            </View>
        )
    },
    (prev, next) => {
        return prev.isSelected === next.isSelected && prev.reportDetails === next.reportDetails
    }
)

export default RowReportDetails
